import { ConnectionListResult, FeatureFlagsContext, GridSortProps, PaginatedQueryWrapper } from '@tableau/data-catalog-shared';
import * as React from 'react';
import { Messages } from '../../../gen-src/Localize';
import { FlowsDisplayGridRowDataFragment } from '../../../gen-src/queries/FlowsDisplayGridRowDataFragment';
import { FlowsDisplayGridRowDataFragmentWithoutPersonalSpace } from '../../../gen-src/queries/FlowsDisplayGridRowDataFragmentWithoutPersonalSpace';
import {
  TableUpstreamFlowsFilteredByColumnQuery,
  TableUpstreamFlowsFilteredByColumnQueryVariables,
} from '../../../gen-src/queries/TableUpstreamFlowsFilteredByColumnQuery';
import {
  TableUpstreamFlowsFilteredByColumnWithoutPersonalSpaceQuery,
  TableUpstreamFlowsFilteredByColumnWithoutPersonalSpaceQueryVariables,
} from '../../../gen-src/queries/TableUpstreamFlowsFilteredByColumnWithoutPersonalSpaceQuery';
import { TableUpstreamFlowsQuery, TableUpstreamFlowsQueryVariables } from '../../../gen-src/queries/TableUpstreamFlowsQuery';
import { TableUpstreamFlowsWithoutPersonalSpaceQuery, TableUpstreamFlowsWithoutPersonalSpaceQueryVariables } from '../../../gen-src/queries/TableUpstreamFlowsWithoutPersonalSpaceQuery';
import { FlowSortOrder } from '../../../gen-src/queries/globalTypes';
import { TableUpstreamFlowsFilteredByColumnQueryGql } from '../../queries/TableUpstreamFlowsFilteredByColumnQueryGql';
import { TableUpstreamFlowsFilteredByColumnWithoutPersonalSpaceQueryGql } from '../../queries/TableUpstreamFlowsFilteredByColumnWithoutPersonalSpaceQueryGql';
import { TableUpstreamFlowsQueryGql } from '../../queries/TableUpstreamFlowsQueryGql';
import { TableUpstreamFlowsWithoutPersonalSpaceQueryGql } from '../../queries/TableUpstreamFlowsWithoutPersonalSpaceQueryGql';
import {
  FlowsDisplayGrid,
  FlowsDisplayGridRowData,
  getRowDataForFlow,
  getRowDataForFlowWithoutPersonalSpace,
} from '../sharedDisplayGrid/FlowsDisplayGrid';
import { FlowsValidSortFields } from '../sorting/sortProviders/FlowsGridSortProvider';

type QueryVariables = TableUpstreamFlowsQueryVariables;
type QueryResults = TableUpstreamFlowsQuery;
type QueryResultListNode = FlowsDisplayGridRowDataFragment;
type GridRowData = FlowsDisplayGridRowData;

type QueryVariablesWithoutPersonalSpace = TableUpstreamFlowsWithoutPersonalSpaceQueryVariables;
type QueryResultsWithoutPersonalSpace = TableUpstreamFlowsWithoutPersonalSpaceQuery;
type QueryResultListNodeWithoutPersonalSpace = FlowsDisplayGridRowDataFragmentWithoutPersonalSpace;

type QueryVariablesWithFilter = TableUpstreamFlowsFilteredByColumnQueryVariables;
type QueryResultsWithFilter = TableUpstreamFlowsFilteredByColumnQuery;

type QueryVariablesWithFilterWithoutPersonalSpace = TableUpstreamFlowsFilteredByColumnWithoutPersonalSpaceQueryVariables;
type QueryResultsWithFilterWithoutPersonalSpace = TableUpstreamFlowsFilteredByColumnWithoutPersonalSpaceQuery;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  const table = resultData && resultData.tables && resultData.tables[0];
  return table.upstreamFlowsConnection;
}

function selectConnectionListResultWithoutPersonalSpace(resultData: QueryResultsWithoutPersonalSpace):
  ConnectionListResult<QueryResultListNodeWithoutPersonalSpace> | null {
  const table = resultData && resultData.tables && resultData.tables[0];
  return table.upstreamFlowsConnection;
}

function selectConnectionListResultForFilteredQuery(resultData: QueryResultsWithFilter): ConnectionListResult<QueryResultListNode> | null {
  const table = resultData && resultData.tables && resultData.tables[0];
  const column = table && table.columns && table.columns[0];
  return column.upstreamFlowsConnection;
}

function selectConnectionListResultForFilteredQueryWithoutPersonalSpace(resultData: QueryResultsWithFilterWithoutPersonalSpace):
  ConnectionListResult<QueryResultListNodeWithoutPersonalSpace> | null {
  const table = resultData && resultData.tables && resultData.tables[0];
  const column = table && table.columns && table.columns[0];
  return column.upstreamFlowsConnection;
}

export type TableUpstreamFlowsQueryGridProps = GridSortProps<FlowsValidSortFields> & {
  readonly tableId: string;
  readonly filteredColumnId: string | null;
  readonly rowCount: number | null;
};

export function TableUpstreamFlowsQueryGrid(props: TableUpstreamFlowsQueryGridProps): JSX.Element {
  const { personalSpaceEnabled } = React.useContext(FeatureFlagsContext);
  const emptyGridMessage = Messages.EmptyUpstreamFlowsLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyUpstreamFilteredFlowsLineageGridMessage();

  return personalSpaceEnabled ? (
    props.filteredColumnId ? (
      <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
        queryGql={TableUpstreamFlowsFilteredByColumnQueryGql}
        partialQueryVariables={{ tableId: props.tableId, columnId: props.filteredColumnId, orderBy: props.orderBy as FlowSortOrder }}
        selectConnectionListResult={selectConnectionListResultForFilteredQuery}
        mapResultNode={getRowDataForFlow}
      >
        {({ rowStore }) => (
          <FlowsDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyFilteredGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        )}
      </PaginatedQueryWrapper>
    ) : (
      <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
        queryGql={TableUpstreamFlowsQueryGql}
        partialQueryVariables={{ tableId: props.tableId, orderBy: props.orderBy as FlowSortOrder }}
        selectConnectionListResult={selectConnectionListResult}
        mapResultNode={getRowDataForFlow}
      >
        {({ rowStore }) => (
          <FlowsDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        )}
      </PaginatedQueryWrapper>
    )
  ) : (
    props.filteredColumnId ? (
      <PaginatedQueryWrapper<QueryVariablesWithFilterWithoutPersonalSpace, QueryResultsWithFilterWithoutPersonalSpace,
      QueryResultListNodeWithoutPersonalSpace, GridRowData>
        queryGql={TableUpstreamFlowsFilteredByColumnWithoutPersonalSpaceQueryGql}
        partialQueryVariables={{ tableId: props.tableId, columnId: props.filteredColumnId, orderBy: props.orderBy as FlowSortOrder }}
        selectConnectionListResult={selectConnectionListResultForFilteredQueryWithoutPersonalSpace}
        mapResultNode={getRowDataForFlowWithoutPersonalSpace}
      >
        {({ rowStore }) => (
          <FlowsDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyFilteredGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        )}
      </PaginatedQueryWrapper>
    ) : (
      <PaginatedQueryWrapper<QueryVariablesWithoutPersonalSpace, QueryResultsWithoutPersonalSpace,
      QueryResultListNodeWithoutPersonalSpace, GridRowData>
        queryGql={TableUpstreamFlowsWithoutPersonalSpaceQueryGql}
        partialQueryVariables={{ tableId: props.tableId, orderBy: props.orderBy as FlowSortOrder }}
        selectConnectionListResult={selectConnectionListResultWithoutPersonalSpace}
        mapResultNode={getRowDataForFlowWithoutPersonalSpace}
      >
        {({ rowStore }) => (
          <FlowsDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        )}
      </PaginatedQueryWrapper>
    )
  );
}
